import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Lenis from 'lenis'

let gsapRegistered = false
let lenis: Lenis | null = null

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export function initGSAP(): typeof gsap {
  if (!gsapRegistered) {
    gsap.registerPlugin(ScrollTrigger)
    gsapRegistered = true
  }
  return gsap
}

export function getLenis(): Lenis | null {
  return lenis
}

export function initLenis(): Lenis | null {
  if (lenis || prefersReducedMotion()) return lenis
  const gsapApi = initGSAP()
  lenis = new Lenis({
    duration: 1.15,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    touchMultiplier: 1.3,
  })
  lenis.on('scroll', () => {
    ScrollTrigger.update()
  })
  gsapApi.ticker.add((time) => {
    lenis?.raf(time * 1000)
  })
  gsapApi.ticker.lagSmoothing(0)
  return lenis
}

export function stopLenis(): void {
  lenis?.stop()
  document.documentElement.classList.add('lenis-stopped')
}

export function startLenis(): void {
  lenis?.start()
  document.documentElement.classList.remove('lenis-stopped')
}

export function destroyLenis(): void {
  lenis?.destroy()
  lenis = null
}

export function scrollToSection(target: string, offset = -16): void {
  const element = document.querySelector(target)
  if (!element) return
  if (lenis) {
    lenis.scrollTo(element as HTMLElement, { offset, duration: 1.2 })
  } else if (prefersReducedMotion()) {
    element.scrollIntoView({ behavior: 'auto' })
  } else {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

export function scrollToTop(smooth = false): void {
  if (lenis) {
    lenis.scrollTo(0, smooth ? { duration: 0.8 } : { immediate: true })
  } else if (smooth && !prefersReducedMotion()) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } else {
    window.scrollTo(0, 0)
  }
}

export function initSlideReveals(
  scope: HTMLElement | Document = document
): () => void {
  const gsapApi = initGSAP()
  const ctx = gsapApi.context(() => {
    gsapApi.utils.toArray<HTMLElement>('[data-reveal]', scope).forEach((el) => {
      const delay = parseFloat(el.dataset.revealDelay || '0')
      gsapApi.fromTo(
        el,
        { y: 32, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.9,
          delay,
          ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 86%', once: true },
        }
      )
    })
  }, scope)
  return () => ctx.revert()
}

export interface PanelRevealOptions {
  drawLine?: boolean
  stagger?: number
  itemSelector?: string
  lineSelector?: string
}

export function setupPanelReveals(
  container: HTMLElement,
  options: PanelRevealOptions = {}
): () => void {
  if (prefersReducedMotion()) return () => {}
  const gsapApi = initGSAP()
  const {
    drawLine = false,
    stagger = 0.1,
    itemSelector = '[data-reveal-item]',
    lineSelector = '[data-timeline-line]',
  } = options
  const ctx = gsapApi.context(() => {
    gsapApi.utils
      .toArray<HTMLElement>(itemSelector, container)
      .forEach((el, i) => {
        gsapApi.fromTo(
          el,
          { opacity: 0, y: 28 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: 'power3.out',
            delay: Math.min(i * stagger, 0.5),
            scrollTrigger: { trigger: el, start: 'top 85%', once: true },
          }
        )
      })
    if (drawLine) {
      gsapApi.fromTo(
        lineSelector,
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: 'none',
          transformOrigin: 'top center',
          scrollTrigger: {
            trigger: container,
            start: 'top 75%',
            end: 'bottom 60%',
            scrub: 0.5,
          },
        }
      )
    }
  }, container)
  return () => ctx.revert()
}